import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, 
  Plus,
  X,
  User,
  Check,
  Scissors,
  Percent,
  Equal,
  Edit3,
  Store,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const SplitBill = ({ user }) => {
  const navigate = useNavigate();
  const [totalAmount, setTotalAmount] = useState("");
  const [note, setNote] = useState("");
  const [splitMethod, setSplitMethod] = useState("equal");
  const [participants, setParticipants] = useState([
    { name: user?.name || "You", contact: user?.email, user_id: user?.user_id, isOwner: true }
  ]);
  const [customSplits, setCustomSplits] = useState({});
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newParticipantName, setNewParticipantName] = useState("");
  const [newParticipantContact, setNewParticipantContact] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [merchantName, setMerchantName] = useState("");
  const [merchantAccount, setMerchantAccount] = useState("");
  const [payToMerchant, setPayToMerchant] = useState(false);

  const totalCents = Math.round(parseFloat(totalAmount || 0) * 100);
  const numParticipants = participants.length;

  // Calculate splits based on method
  const calculateSplits = () => {
    if (splitMethod === "equal") {
      const baseAmount = Math.floor(totalCents / numParticipants);
      const remainder = totalCents % numParticipants;
      return participants.map((_, i) => ({
        participant_index: i,
        amount_cents: baseAmount + (i < remainder ? 1 : 0),
        percent: 100 / numParticipants
      }));
    } else if (splitMethod === "custom") {
      return participants.map((_, i) => ({
        participant_index: i,
        amount_cents: customSplits[i] || 0,
        percent: totalCents > 0 ? ((customSplits[i] || 0) / totalCents) * 100 : 0
      }));
    } else if (splitMethod === "percent") {
      return participants.map((_, i) => {
        const percent = customSplits[i] || 0;
        return {
          participant_index: i,
          amount_cents: Math.round((percent / 100) * totalCents),
          percent: percent
        };
      });
    }
    return [];
  };

  const splits = calculateSplits();
  const totalSplitAmount = splits.reduce((sum, s) => sum + s.amount_cents, 0);
  const totalPercent = splits.reduce((sum, s) => sum + (s.percent || 0), 0);
  
  const isValid = 
    totalCents > 0 && 
    numParticipants >= 2 && 
    (splitMethod === "equal" || 
     (splitMethod === "custom" && totalSplitAmount === totalCents) ||
     (splitMethod === "percent" && Math.abs(totalPercent - 100) < 0.01));

  // Create split bill mutation
  const createSplitBill = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API}/split-bills`, {
        total_amount: totalCents,
        currency: "JMD",
        note: note || null,
        method: splitMethod,
        participants: participants.map(p => ({
          name: p.name,
          contact: p.contact || null,
          user_id: p.user_id || null
        })),
        splits: splitMethod !== "equal" ? splits : null,
        merchant_name: payToMerchant ? merchantName : null,
        merchant_account: payToMerchant ? merchantAccount : null
      });
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to create split bill");
    }
  });

  const addParticipant = () => {
    if (!newParticipantName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    setParticipants([...participants, { 
      name: newParticipantName.trim(), 
      contact: newParticipantContact.trim() || null,
      user_id: null,
      isOwner: false
    }]);
    setNewParticipantName("");
    setNewParticipantContact("");
    setShowAddParticipant(false);
  };

  const removeParticipant = (index) => {
    if (participants[index].isOwner) return;
    const newParticipants = participants.filter((_, i) => i !== index);
    setParticipants(newParticipants);
    const newCustomSplits = {};
    Object.keys(customSplits).forEach(key => {
      const k = parseInt(key);
      if (k < index) newCustomSplits[k] = customSplits[k];
      else if (k > index) newCustomSplits[k - 1] = customSplits[k];
    });
    setCustomSplits(newCustomSplits);
  };

  const updateCustomSplit = (index, value) => {
    const numValue = parseFloat(value) || 0;
    setCustomSplits({
      ...customSplits,
      [index]: splitMethod === "custom" ? Math.round(numValue * 100) : numValue
    });
  };

  const splitMethods = [
    { id: "equal", label: "Equal", icon: Equal },
    { id: "custom", label: "Custom", icon: Edit3 },
    { id: "percent", label: "Percent", icon: Percent },
  ];

  // Success Screen
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-5">
        <div className="text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-emerald-400" />
          </div>
          <h1 className="font-heading font-bold text-2xl text-white mb-2">
            Requests Sent!
          </h1>
          <p className="text-muted-foreground mb-2">
            Split bill for <span className="font-semibold text-white">J${(totalCents / 100).toLocaleString()}</span>
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            {numParticipants} participants notified
          </p>
          <Button
            className="w-full btn-primary h-12"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="split-bill-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="text-white hover:bg-white/10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center">
            <Scissors className="w-4 h-4 text-white" />
          </div>
          <h1 className="font-heading font-semibold text-xl text-white">Split Bill</h1>
        </div>
      </header>

      <main className="px-5 pb-32 max-w-lg mx-auto space-y-6">
        {/* Bill Details Card */}
        <div className="glass rounded-3xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
            Bill Details
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                Total Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted-foreground font-medium">
                  J$
                </span>
                <input
                  type="number"
                  placeholder="0.00"
                  className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 
                    text-xl font-mono text-white focus:outline-none focus:border-pink-500/50"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  data-testid="total-amount-input"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-white mb-2 block">
                Note (optional)
              </label>
              <input
                type="text"
                placeholder="e.g., Dinner at Kingston Grill"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 
                  text-white placeholder:text-muted-foreground focus:outline-none focus:border-pink-500/50"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="note-input"
              />
            </div>
            
            {/* Pay to Merchant Option */}
            <div className="pt-2">
              <button
                onClick={() => setPayToMerchant(!payToMerchant)}
                className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all ${
                  payToMerchant 
                    ? "bg-teal-500/20 border-teal-500/30" 
                    : "bg-white/5 border-white/10 hover:bg-white/10"
                }`}
                data-testid="pay-merchant-toggle"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    payToMerchant ? "bg-teal-500" : "bg-white/10"
                  }`}>
                    <Store className={`w-5 h-5 ${payToMerchant ? "text-white" : "text-muted-foreground"}`} />
                  </div>
                  <div className="text-left">
                    <p className={`font-medium ${payToMerchant ? "text-white" : "text-muted-foreground"}`}>
                      Pay Merchant Directly
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Funds sent to merchant when complete
                    </p>
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  payToMerchant ? "border-teal-400 bg-teal-400" : "border-white/30"
                }`}>
                  {payToMerchant && <Check className="w-3 h-3 text-white" />}
                </div>
              </button>
              
              {payToMerchant && (
                <div className="mt-3 space-y-3 p-4 rounded-xl bg-white/5 border border-white/10">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Merchant Name</label>
                    <input
                      type="text"
                      placeholder="e.g., Kingston Grill Restaurant"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 
                        text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-teal-500/50"
                      value={merchantName}
                      onChange={(e) => setMerchantName(e.target.value)}
                      data-testid="merchant-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Merchant Account / Email</label>
                    <input
                      type="text"
                      placeholder="e.g., payments@kingstongrill.com"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 
                        text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-teal-500/50"
                      value={merchantAccount}
                      onChange={(e) => setMerchantAccount(e.target.value)}
                      data-testid="merchant-account-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Split With Section */}
        <div className="glass rounded-3xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs uppercase tracking-widest text-muted-foreground">
              Split With
            </h2>
            <button
              onClick={() => setShowAddParticipant(true)}
              className="flex items-center gap-1 text-sm font-medium text-pink-400 hover:opacity-80"
              data-testid="add-participant-btn"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          <div className="flex gap-3 flex-wrap">
            {participants.map((participant, index) => (
              <div
                key={index}
                className={`relative flex items-center gap-2 px-3 py-2 rounded-full border transition-all ${
                  participant.isOwner 
                    ? "bg-pink-500/20 border-pink-500/30" 
                    : "bg-white/5 border-white/10"
                }`}
                data-testid={`participant-${index}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                  participant.isOwner ? "bg-pink-500 text-white" : "bg-white/20 text-white"
                }`}>
                  {participant.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-white">
                  {participant.name}
                  {participant.isOwner && " (You)"}
                </span>
                {!participant.isOwner && (
                  <button
                    onClick={() => removeParticipant(index)}
                    className="ml-1 text-muted-foreground hover:text-red-400 transition-colors"
                    data-testid={`remove-participant-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {participants.length < 2 && (
            <p className="text-xs text-yellow-400 mt-3 flex items-center gap-1">
              <span>⚠️</span> Add at least one more person to split with
            </p>
          )}
        </div>

        {/* Split Method Section */}
        <div className="glass rounded-3xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
            Split Method
          </h2>
          
          <div className="flex gap-2">
            {splitMethods.map(method => {
              const Icon = method.icon;
              const isActive = splitMethod === method.id;
              return (
                <button
                  key={method.id}
                  onClick={() => {
                    setSplitMethod(method.id);
                    setCustomSplits({});
                  }}
                  className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${
                    isActive 
                      ? "border-pink-500 bg-pink-500/20" 
                      : "border-white/10 bg-white/5 hover:border-white/20"
                  }`}
                  data-testid={`method-${method.id}`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-pink-400" : "text-muted-foreground"}`} />
                  <span className={`text-xs font-medium ${isActive ? "text-pink-400" : "text-muted-foreground"}`}>
                    {method.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Breakdown Card */}
        <div className="glass rounded-3xl p-5">
          <h2 className="text-xs uppercase tracking-widest text-muted-foreground mb-4">
            Breakdown
          </h2>

          <div className="space-y-3">
            {participants.map((participant, index) => {
              const split = splits[index] || { amount_cents: 0, percent: 0 };
              return (
                <div 
                  key={index} 
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                      participant.isOwner ? "bg-pink-500 text-white" : "bg-white/20 text-white"
                    }`}>
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-white">{participant.name}</span>
                  </div>
                  
                  {splitMethod === "equal" ? (
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold text-white">
                        J${(split.amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {split.percent.toFixed(1)}%
                      </p>
                    </div>
                  ) : splitMethod === "custom" ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">J$</span>
                      <input
                        type="number"
                        placeholder="0.00"
                        className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 
                          text-sm font-mono text-right text-white focus:outline-none focus:border-pink-500/50"
                        value={customSplits[index] ? (customSplits[index] / 100).toFixed(2) : ""}
                        onChange={(e) => updateCustomSplit(index, e.target.value)}
                        data-testid={`custom-amount-${index}`}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="0"
                        className="w-16 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 
                          text-sm font-mono text-right text-white focus:outline-none focus:border-pink-500/50"
                        value={customSplits[index] || ""}
                        onChange={(e) => updateCustomSplit(index, e.target.value)}
                        data-testid={`percent-amount-${index}`}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Validation feedback */}
          {splitMethod === "custom" && totalCents > 0 && totalSplitAmount !== totalCents && (
            <p className="text-xs text-red-400 mt-3">
              Amounts must total J${(totalCents / 100).toFixed(2)} (current: J${(totalSplitAmount / 100).toFixed(2)})
            </p>
          )}
          {splitMethod === "percent" && Math.abs(totalPercent - 100) > 0.01 && (
            <p className="text-xs text-red-400 mt-3">
              Percentages must total 100% (current: {totalPercent.toFixed(1)}%)
            </p>
          )}
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-5 glass-heavy border-t border-white/10">
        <div className="max-w-lg mx-auto">
          <Button
            className="w-full btn-primary h-12"
            disabled={!isValid || createSplitBill.isPending}
            onClick={() => createSplitBill.mutate()}
            data-testid="send-request-btn"
          >
            {createSplitBill.isPending ? "Sending..." : "Send Request"}
          </Button>
        </div>
      </div>

      {/* Add Participant Modal */}
      {showAddParticipant && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowAddParticipant(false)}
        >
          <div 
            className="w-full max-w-sm glass-heavy rounded-3xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg text-white">Add Participant</h3>
              <button 
                onClick={() => setShowAddParticipant(false)}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-white mb-2 block">Name</label>
                <input
                  type="text"
                  placeholder="Enter name"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 
                    text-white placeholder:text-muted-foreground focus:outline-none focus:border-pink-500/50"
                  value={newParticipantName}
                  onChange={(e) => setNewParticipantName(e.target.value)}
                  data-testid="new-participant-name"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-white mb-2 block">
                  Email or Phone (optional)
                </label>
                <input
                  type="text"
                  placeholder="email@example.com or phone"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 
                    text-white placeholder:text-muted-foreground focus:outline-none focus:border-pink-500/50"
                  value={newParticipantContact}
                  onChange={(e) => setNewParticipantContact(e.target.value)}
                  data-testid="new-participant-contact"
                />
              </div>
              
              <Button
                className="w-full btn-primary h-12"
                onClick={addParticipant}
                data-testid="confirm-add-participant"
              >
                Add Participant
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SplitBill;
